import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * 9A — one-time welcome interstitial.
 *
 * Shown ONCE PER VISITOR PER DEALERSHIP (localStorage, keyed by org) — not
 * once per vehicle, so browsing five cars at the same dealer shows it once.
 * One tap to enter. Manager-toggleable, default on.
 *
 * The copy is PER DEALERSHIP: `copy` carries the GM's overrides from Showroom
 * Settings and every field independently falls back to the shipped trilingual
 * default, so a dealership that never edits it sees exactly today's screen.
 */
export const welcomeKey = (orgId) => `yot_welcome_seen_${orgId}`;

export const wasWelcomeSeen = (orgId) => {
    if (!orgId) return true;
    try { return localStorage.getItem(welcomeKey(orgId)) === '1'; } catch { return true; }
};

export const markWelcomeSeen = (orgId) => {
    try { localStorage.setItem(welcomeKey(orgId), '1'); } catch { /* private mode */ }
};

export const WelcomeInterstitial = ({ dealership, dealerLogo, copy, onEnter, th }) => {
    const { t } = useTranslation();
    const c = copy || {};
    const signature = (c.signature_name || '').trim();
    return (
        <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center px-6 text-center ${th.page}`}
            data-testid="buyer-welcome-screen">
            <div className="max-w-sm space-y-5">
                {dealerLogo && (
                    <img src={dealerLogo} alt={dealership}
                        className="h-10 w-auto max-w-[160px] object-contain mx-auto"
                        data-testid="buyer-welcome-logo" />
                )}
                <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${th.dealership}`}
                    data-testid="buyer-welcome-dealership">{dealership}</p>
                <h1 className={`text-2xl font-extrabold leading-snug ${th.title}`} data-testid="buyer-welcome-title">
                    {c.title || t('welcomeScreen.title', 'We built this to make finding your next vehicle easy.')}
                </h1>
                <p className={`text-base ${th.muted}`} data-testid="buyer-welcome-body">
                    {(c.body || t('welcomeScreen.body', "We'd love your feedback at the end."))}
                    <span className="text-red-500">*</span>
                </p>
                {signature && (
                    <div className="space-y-0.5" data-testid="buyer-welcome-signature">
                        <p className={`text-sm font-semibold ${th.title}`}>{signature}</p>
                        {c.signature_title && (
                            <p className={`text-xs ${th.muted}`} data-testid="buyer-welcome-signature-title">
                                {c.signature_title}
                            </p>
                        )}
                    </div>
                )}
                {/* FIXED footnote — shown with the shipped default copy AND
                    with a per-dealer override, and never part of the editable
                    text, so it cannot be removed by accident. */}
                <p className="text-[11px] leading-relaxed text-red-500" data-testid="buyer-welcome-privacy">
                    {t('welcomeScreen.privacyFootnote')}
                </p>
                <Button onClick={onEnter} data-testid="buyer-welcome-enter"
                    className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-neutral-950 text-base font-bold">
                    {t('welcomeScreen.enter', 'Enter')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
};

export default WelcomeInterstitial;
